import { trackEvent } from './analytics';

/** The session-loss reasons the shell itself observes. The remotes own the rest and report them
 *  through `@rv-life/web-utils`. */
export const HOST_SESSION_LOSS_REASONS = ['boot_backstop', 'middleware_redirect'] as const;

export type HostSessionLossReason = (typeof HOST_SESSION_LOSS_REASONS)[number];

export const SESSION_LOST_COOKIE = 'rvl_session_lost';

const MAX_CORRELATION_ID_LENGTH = 64;
const CORRELATION_ID_PATTERN = /^[A-Za-z0-9-]+$/;

const isHostReason = (value: string): value is HostSessionLossReason =>
	(HOST_SESSION_LOSS_REASONS as readonly string[]).includes(value);

// Each shell call site already fires once per occurrence — the boot backstop is guarded by the
// overlay timeout's reportedStallRef, and the middleware marker is expired as it is read. This
// only drops a repeat of an id already reported, so one reason can never suppress another.
const RECENT_ID_LIMIT = 10;
let recentCorrelationIds: string[] = [];

/** Forgets every id reported so far, so the next report of one of them emits again. */
export const resetSessionLossReporting = (): void => {
	recentCorrelationIds = [];
};

const newCorrelationId = (): string => {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Math.random().toString(36).slice(2, 10)}${Math.random()
			.toString(36)
			.slice(2, 10)}`;
	}
};

/**
 * Reports one authenticated-to-lost transition the shell observed.
 *
 * @param reason Which transition this is.
 * @param correlationId Id minted elsewhere for the same transition. Omit to mint one.
 * @returns The correlation id emitted, or `null` when that id was already reported.
 */
export const reportSessionLoss = (
	reason: HostSessionLossReason,
	correlationId?: string
): string | null => {
	if (correlationId !== undefined && recentCorrelationIds.includes(correlationId)) return null;

	const id = correlationId ?? newCorrelationId();
	recentCorrelationIds = [...recentCorrelationIds, id].slice(-RECENT_ID_LIMIT);
	trackEvent('logout', { reason, correlation_id: id });

	return id;
};

/**
 * Reads the one-shot cookie the middleware leaves on a stale-token redirect and expires it.
 *
 * The cookie is readable by page scripts, so its contents are untrusted: a reason outside the
 * host set or a correlation id that is not a bounded id-like token is discarded rather than
 * forwarded to analytics.
 *
 * @returns The reason and correlation id the middleware recorded, or `null` when there is none
 * to report.
 */
export const takeSessionLostCookie = (): {
	reason: HostSessionLossReason;
	correlationId: string;
} | null => {
	if (typeof document === 'undefined') return null;

	const entry = document.cookie
		.split('; ')
		.find((pair) => pair.startsWith(`${SESSION_LOST_COOKIE}=`));

	if (!entry) return null;

	document.cookie = `${SESSION_LOST_COOKIE}=; Path=/; Max-Age=0`;

	const segments = entry.slice(`${SESSION_LOST_COOKIE}=`.length).split('.');

	if (segments.length !== 2) return null;

	const [reason, correlationId] = segments;

	if (!reason || !isHostReason(reason)) return null;
	if (
		!correlationId ||
		correlationId.length > MAX_CORRELATION_ID_LENGTH ||
		!CORRELATION_ID_PATTERN.test(correlationId)
	) {
		return null;
	}

	return { reason, correlationId };
};
